import { isSuperuserUser } from "utils/dashboardScope";

export const DPAT_FULL_ACCESS_USERNAMES = [
  "ofsam",
  "ofsam1",
  "ofsam2",
  "ofsam3",
  "msow",
  "ramsonao",
  "ayeboah",
  "fkwakye",
  "kofi",
  "harold",
  "Gameli",
  "amoah",
  "JQuarshie",
  "SethPassah",
];

export const CONTEXT_MODULES = [
  {
    slug: "dashboard",
    label: "Dashboard",
    icon: "LayoutGrid",
    navArea: "top",
    allowedLevels: ["national", "region", "district", "subdistrict"],
    description: "Role-aware dashboard for the selected location context",
  },
  {
    slug: "dpat-assessment",
    label: "DPAT Assessment",
    icon: "ClipboardCheck",
    navArea: "top",
    allowedLevels: ["district"],
    assessmentUsersOnly: true,
    description: "District performance assessment records and indicators",
  },
  {
    slug: "performance-assessment",
    label: "Performance Setup",
    icon: "ClipboardCheck",
    navArea: "top",
    allowedLevels: ["region"],
    description: "Regional performance setup by period and indicator selection",
  },
  {
    slug: "performance-monitoring",
    label: "Performance Monitoring",
    icon: "Activity",
    navArea: "top",
    allowedLevels: ["district"],
    description: "District-level performance monitoring workspace",
  },
  {
    slug: "consolidated-monitoring",
    label: "Consolidated Monitoring",
    icon: "Layers3",
    navArea: "top",
    allowedLevels: ["region"],
    description: "Regional consolidated monitoring workspace",
  },
  {
    slug: "reports",
    label: "APR Dashboard",
    icon: "TrendingUp",
    navArea: "top",
    allowedLevels: ["national"],
    description: "Annual progress reporting dashboard and outputs",
  },
  {
    slug: "dpat-dashboard",
    label: "DPAT Dashboard",
    icon: "BarChart3",
    navArea: "top",
    allowedLevels: ["national"],
    allowedUsernames: DPAT_FULL_ACCESS_USERNAMES,
    description: "National DPAT KPIs, rankings, and analytics by Overall Score",
  },
  {
    slug: "dpat-petitions",
    label: "DPAT Petitions",
    icon: "FileText",
    navArea: "top",
    allowedLevels: ["national"],
    hideFromTopNav: true,
    description: "National district petition review queue",
  },
  {
    slug: "progress-report",
    label: "MMDA Progress Report",
    icon: "FileText",
    navArea: "top",
    allowedLevels: ["district", "subdistrict"],
    description: "Reporting workflow, submission tracking, and review queues",
  },
  {
    slug: "rcc-progress-report",
    label: "RCC Progress Report",
    icon: "FileText",
    navArea: "top",
    allowedLevels: ["region"],
    description: "Regional Progress Report tracking, vews and analysis",
  },
  
  // {
  //   slug: "rcc",
  //   label: "RCC",
  //   icon: "Network",
  //   navArea: "top",
  //   allowedLevels: ["region"],
  //   description: "Regional RCC views",
  // },


  {
    slug: "ohlgs-report",
    label: "Performance Contract",
    icon: "FileText",
    navArea: "top",
    allowedLevels: [ "district"],
    description: "Regional RCC OHLGS assessment workspace and district OHLGS reporting",
  },


    {
    slug: "ohlgs-regional-report",
    label: "Performance Contract Regional",
    icon: "FileText",
    navArea: "top",
    allowedLevels: ["region" ],
    description: "Regional RCC OHLGS assessment workspace and district OHLGS reporting",
  },

  {
    slug: "ohlgs-consolidated",
    label: "Consolidated Performance Contract ",
    icon: "FileText",
    navArea: "top",
    allowedLevels: ["national" ],
    description: "Regional RCC OHLGS consolidated reporting",
  },

  {
    slug: "trackers",
    label: "Trackers",
    icon: "ClipboardList",
    navArea: "top",
    allowedLevels: ["region", "district", "subdistrict"],
    // superuserOnly: true,
    description: "Operational trackers and meeting workflows",
  },
  
  {
    slug: "settings",
    label: "Settings",
    icon: "Settings",
    navArea: "top",
    hideFromTopNav: true,
    allowedLevels: ["national", "region", "district", "subdistrict"],
    description: "User account and access settings",
  },
  {
    slug: "users-access",
    label: "Users & Access",
    icon: "ShieldCheck",
    navArea: "sidebar",
    hideFromTopNav: true,
    superuserOnly: true,
    allowedLevels: ["national", "region", "district", "subdistrict"],
    description: "Administrative user, role, permission, and access oversight",
  },
];

export const CONTEXT_SIDEBAR_MODULES = CONTEXT_MODULES.filter((moduleConfig) => moduleConfig.navArea === "sidebar");
export const CONTEXT_TOP_NAV_MODULES = CONTEXT_MODULES.filter((moduleConfig) => moduleConfig.navArea !== "sidebar");

export const CONTEXT_MODULES_BY_SLUG = CONTEXT_MODULES.reduce((acc, moduleConfig) => {
  acc[moduleConfig.slug] = moduleConfig;
  return acc;
}, {});

function normalizeUsername(user) {
  return String(user?.user?.username || "").trim().toLowerCase();
}

function userRoleNames(user) {
  return (user?.user?.userRoles || []).map((role) => String(role?.name || "").trim());
}

function userHasRole(user, roleName) {
  const wanted = String(roleName || "").trim().toLowerCase();
  return userRoleNames(user).some((name) => name.toLowerCase() === wanted);
}

export function hasDpatFullScoreAccess(user) {
  if (isSuperuserUser(user)) return true;
  return userHasRole(user, "Technical Working Group");
}

export function canViewDpatScoreSheets(user) {
  if (isSuperuserUser(user)) return true;
  return userHasRole(user, "Technical Working Group") || userHasRole(user, "DPAT DISTRICT USERS");
}

export function isDpatDistrictRestrictedViewer(user) {
  return Boolean(userHasRole(user, "DPAT DISTRICT USERS") && !hasDpatFullScoreAccess(user));
}

function normalizeLocationKey(value) {
  return String(value || "").trim().toLowerCase();
}

function locationAccessKeys(currentLocation) {
  return new Set(
    [
      currentLocation?.orgUnitId,
      currentLocation?.id,
      currentLocation?.slug,
      currentLocation?.name,
      currentLocation?.districtName,
    ]
      .map((value) => normalizeLocationKey(value))
      .filter(Boolean),
  );
}

function assessmentUserHasLocationAccess(assessmentUsers, username, currentLocation) {
  const locationKeys = locationAccessKeys(currentLocation);

  return assessmentUsers.some((item) => {
    if (normalizeLocationKey(item?.username) !== username) {
      return false;
    }

    const accessLevel = normalizeLocationKey(item?.level);
    if (!accessLevel) return false;
    if (accessLevel === "all") return true;
    return locationKeys.has(accessLevel);
  });
}

export function isModuleAllowedForLevel(
  moduleSlug,
  level,
  user = null,
  assessmentUsers = [],
  currentLocation = null,
) {
  const moduleConfig = CONTEXT_MODULES_BY_SLUG[moduleSlug];
  if (!moduleConfig || !moduleConfig.allowedLevels.includes(level)) {
    return false;
  }

  if (moduleConfig.superuserOnly && !isSuperuserUser(user)) {
    return false;
  }

  if (moduleConfig.assessmentUsersOnly) {
    const username = normalizeUsername(user);
    const isPetitionReviewer =
      isSuperuserUser(user) ||
      userHasRole(user, "DPAT PETITION COMMITTEE") ||
      userHasRole(user, "DPAT TECHNICAL TEAM") ||
      userHasRole(user, "DPAT QUALITY ASSURANCE") ||
      DPAT_FULL_ACCESS_USERNAMES.map((name) => name.toLowerCase()).includes(username);
    const hasDistrictAccess = assessmentUserHasLocationAccess(
      assessmentUsers,
      username,
      currentLocation,
    );

    if (!username || (!hasDistrictAccess && !isPetitionReviewer)) {
      return false;
    }
  }

  if (moduleConfig.allowedUsernames?.length) {
    const username = normalizeUsername(user);
    const allowed = moduleConfig.allowedUsernames.map((name) => name.toLowerCase());
    if (!username || !allowed.includes(username)) {
      return false;
    }
  }

  return true;
}

export function getDefaultModuleForLevel(
  level,
  user = null,
  assessmentUsers = [],
  currentLocation = null,
) {
  const firstAllowed = CONTEXT_MODULES.find((moduleConfig) =>
    isModuleAllowedForLevel(
      moduleConfig.slug,
      level,
      user,
      assessmentUsers,
      currentLocation,
    ),
  );
  return firstAllowed?.slug || "dashboard";
}

export function buildContextPath(locationNode, moduleSlug) {
  const safeModule = moduleSlug || "dashboard";

  if (!locationNode || locationNode.level === "national") {
    return `/ghana/${safeModule}`;
  }

  if (locationNode.level === "region") {
    return `/ghana/${locationNode.slug}/${safeModule}`;
  }

  if (locationNode.level === "district") {
    return `/ghana/${locationNode.regionSlug}/${locationNode.slug}/${safeModule}`;
  }

  if (locationNode.level === "subdistrict") {
    return `/ghana/${locationNode.regionSlug}/${locationNode.districtSlug}/${locationNode.slug}/${safeModule}`;
  }

  return `/ghana/${safeModule}`;
}
